import CryptoJS from 'crypto-js'

import { sendBotMessage } from '../helpers/bot'

export const encryptToken = (token, key) => {
  const iv = CryptoJS.lib.WordArray.random(16)

  const encrypted = CryptoJS.AES.encrypt(token, key, { iv })

  const combined = iv.toString() + encrypted.toString()

  return combined
}

export const decryptToken = (encrypted, key) => {
  const iv = CryptoJS.enc.Hex.parse(encrypted.slice(0, 32))

  const encryptedToken = encrypted.slice(32)

  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedToken, key, { iv })

    return decrypted.toString(CryptoJS.enc.Utf8)
  } catch (error) {
    sendBotMessage(`Error en la función decryptToken: ${error}`, `Token: ${encrypted}`)
    console.error('Error durante la desencriptación:', error)
    return null
  }
}

export const resolveMapboxToken = (token) => {
  if (!token) return process.env.REACT_APP_MAPBOX_TOKEN
  if (token.startsWith('pk.')) return token
  return (
    decryptToken(token, process.env.REACT_APP_TOKEN_ENCRYPTATION_KEY) ||
    process.env.REACT_APP_MAPBOX_TOKEN
  )
}

export const encryptData = (data, key) => {
  const iv = CryptoJS.lib.WordArray.random(16)

  const encrypted = CryptoJS.AES.encrypt(data, CryptoJS.enc.Hex.parse(key), {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  })

  const encryptedData = iv.concat(encrypted.ciphertext).toString(CryptoJS.enc.Hex)

  return encryptedData
}

export const decryptSecretKey = (secretKey, encryptionKey) => {
  if (!encryptionKey || encryptionKey.length !== 64) {
    throw new Error('ERROR_AUTH.INVALID_KEY')
  }

  try {
    const ivHex = secretKey.slice(0, 32)
    const encryptedDataHex = secretKey.slice(32)

    const iv = CryptoJS.enc.Hex.parse(ivHex)
    const encryptedData = CryptoJS.enc.Hex.parse(encryptedDataHex)

    const key = CryptoJS.enc.Hex.parse(encryptionKey)

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encryptedData },
      key,
      { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    )

    return CryptoJS.enc.Utf8.stringify(decrypted)
  } catch (error) {
    throw new Error('ERROR_AUTH.INVALID_KEY')
  }
}

export const encryptSecretKey = (data, encryptionKey) => {
  if (!data) return null

  if (!encryptionKey || encryptionKey.length !== 64) {
    throw new Error('ERROR_AUTH.INVALID_KEY')
  }

  const iv = CryptoJS.lib.WordArray.random(16)

  const key = CryptoJS.enc.Hex.parse(encryptionKey)

  const dataStr = String(data)

  const encrypted = CryptoJS.AES.encrypt(dataStr, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7// Mismo padding que Node crypto
  })

  const ivHex = iv.toString(CryptoJS.enc.Hex)

  const encryptedHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex)

  return ivHex + encryptedHex
}

export const encryptOtpData = (data, keyHex) => {
  if (!data) return null

  if (!keyHex || keyHex.length !== 64) {
    throw new Error('Invalid key')
  }

  const key = CryptoJS.enc.Hex.parse(keyHex)

  const iv = CryptoJS.lib.WordArray.random(16)

  const encrypted = CryptoJS.AES.encrypt(data, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  })

  const ivHex = iv.toString(CryptoJS.enc.Hex)
  const cipherTextHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex)

  return ivHex + cipherTextHex
}

const pemToArrayBuffer = (pem) => {
  const b64 = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '')

  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes.buffer
}

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  bytes.forEach(b => { binary += String.fromCharCode(b) })

  return btoa(binary)
}

export const encryptWithRsaPublicKey = async (data, publicKeyBase64) => {
  const pem = atob(publicKeyBase64)

  const binaryDer = pemToArrayBuffer(pem)

  const publicKey = await window.crypto.subtle.importKey(
    'spki',
    binaryDer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['encrypt']
  )

  const encoded = new TextEncoder().encode(JSON.stringify(data))

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    encoded
  )
  return arrayBufferToBase64(encrypted)
}
